import { Link } from "@tanstack/react-router";
import { formatMoney, type LicenseTier } from "@/lib/bkls";
import { beatMetaLine, startingPriceCents, type MarketplaceBeat } from "@/lib/bkls-marketplace";

interface Props {
  beat: MarketplaceBeat;
  tiers?: LicenseTier[];
  /** Factual, attribute-derived context — e.g. why a beat is recommended. */
  reasons?: string[];
  onSelect?: () => void;
  onPreview?: () => void;
}

export function BeatCard({ beat, tiers = [], reasons, onSelect, onPreview }: Props) {
  const from = startingPriceCents(beat, tiers);

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-2xl border hairline bg-charcoal transition-colors focus-within:border-gold hover:border-gold/60">
      <Link
        to="/beats/$slug"
        params={{ slug: beat.slug }}
        onClick={onSelect}
        className="block focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold"
      >
        <div className="aspect-square w-full bg-obsidian">
          {beat.artworkUrl ? (
            <img
              src={beat.artworkUrl}
              alt={`Cover artwork for the beat ${beat.title}`}
              loading="lazy"
              decoding="async"
              className="h-full w-full object-cover"
            />
          ) : (
            <div aria-hidden="true" className="h-full w-full bg-gradient-to-br from-obsidian to-charcoal" />
          )}
        </div>
        <div className="p-5">
          <h3 className="font-serif text-xl text-ivory group-hover:text-gold-luminous">{beat.title}</h3>
          <p className="mt-1 text-xs text-muted-foreground">{beatMetaLine(beat) || "Original production"}</p>
          {reasons?.length ? (
            <p className="mt-2 text-[10px] uppercase tracking-[0.2em] text-gold/80">{reasons.join(" · ")}</p>
          ) : null}
          <p className="mt-3 text-sm text-gold">
            {from ? `From ${formatMoney(from)}` : "Licence by request"}
          </p>
        </div>
      </Link>

      {beat.previewUrl && (
        <div className="mt-auto px-5 pb-5">
          <audio
            controls
            preload="none"
            src={beat.previewUrl}
            onPlay={onPreview}
            aria-label={`Tagged preview of ${beat.title}`}
            className="w-full"
          >
            <track kind="captions" />
          </audio>
        </div>
      )}
    </article>
  );
}
